/**
 * The Smart Pen (SRS C5): a rough square becomes a clean square.
 *
 * The teacher draws and lifts. A quarter of a second later the stroke is scored
 * against the shape library, and if the answer is sure enough the ink is taken off
 * the board and the clean shape slides into its place over 150 ms.
 *
 * Three rules shape this file:
 *
 *  - Nothing here is ever in the way of the hand. Every decision happens after the
 *    pen is already up (docs/03-ARCHITECTURE.md §7.2).
 *  - The board changes once, not twice. The ink leaving and the shape arriving are
 *    written as a single edit, so it reaches everyone else — and the undo stack — as
 *    one thing that happened, not as a deletion followed by a mystery shape.
 *  - It only ever asks the recogniser a question. It never looks at how the answer
 *    was arrived at, which is what keeps SRS C5.1's promise that the recogniser can
 *    be replaced without touching the pen.
 *
 * What must never snap — handwriting, tiny marks, the highlighter — is decided in
 * snap-guards.ts, which is asked before the recogniser ever is.
 */

import {
  SNAP_EVALUATION_WINDOW_MS,
  SNAP_MORPH_DURATION_MS,
  UNDO_SNAP_PILL_DURATION_MS,
  type CanvasLayerName,
  type ShapeRecogniser,
  type ViewportState,
} from '@opal/contracts';
import { newStrokeId, type BoardObject, type Point, type Stroke, type Unsubscribe } from '@opal/shared';

import { NO_EDIT, type BoardEdit } from './board-edit.js';
import { strokePoints } from './ink.js';
import {
  drawnWithHighlighter,
  looksLikeWriting,
  tooSmallToSnap,
  WRITING_MEMORY_MS,
  type RecentInk,
} from './snap-guards.js';
import { strokeBounds } from './stroke-bounds.js';
import type { ObjectBoard } from './object-board.js';
import { animationFrames, type FrameScheduler } from './paint-scheduler.js';
import { drawOutline, type OutlineStyle } from './shape-paint.js';
import { buildShape } from './shape-object.js';
import { cleanShapeOf, morphBetween } from './snap-shape.js';
import { toViewport } from './viewport-state.js';

/** The ink a snap takes away — and puts back, when the teacher says it got it wrong. */
export interface SnapInk {
  find(strokeId: string): Stroke | null;
  remove(strokeId: string): boolean;
  accept(stroke: Stroke): void;
}

/**
 * The offer of a way back, for the four seconds it stands (SRS C5).
 *
 * The screen is handed the shape so it knows where to put the pill, and the way to
 * undo it. It is never handed anything about how the snap was decided.
 */
export interface SnapOffer {
  readonly object: BoardObject;
  /** Takes the shape away and puts the original ink back, for good. */
  readonly undo: () => void;
}

/** The wait between the pen lifting and the shape being judged. Injected for tests. */
export interface Timer {
  set(run: () => void, ms: number): number;
  clear(handle: number): void;
}

export const systemTimer: Timer = {
  set: (run, ms) => window.setTimeout(run, ms),
  clear: (handle) => {
    window.clearTimeout(handle);
  },
};

export interface SmartPenOptions {
  /** SRS C5.1 — asked, never inspected. */
  readonly recogniser: ShapeRecogniser;
  /** SRS K3 — read fresh every time, so a changed setting takes effect at once. */
  readonly confidence: () => number;
  /** The Smart Pen toggle. Read at the moment of judging, not when drawing began. */
  readonly enabled: () => boolean;
  readonly ink: SnapInk;
  readonly objects: ObjectBoard;
  readonly onChanged: (layer: CanvasLayerName) => void;
  readonly now?: () => number;
  readonly frames?: FrameScheduler;
  readonly timer?: Timer;
  /** docs/03-ARCHITECTURE.md §7.4 — no morph for anyone who asked for less motion. */
  readonly reducedMotion?: () => boolean;
  /**
   * SRS C5 — a shape has just snapped, and for four seconds there is a way back.
   *
   * Handed to the screen rather than drawn here: the pill is a button, and a button
   * painted on a canvas has nothing to press, no focus ring and no keyboard.
   */
  readonly onSnapped?: (offer: SnapOffer | null) => void;
}

export interface SmartPen {
  /** A stroke was finished here. Judged when the evaluation window is up. */
  consider(stroke: Stroke): void;
  /** Draws the shape mid-morph. Nothing at all when none is in flight. */
  paintOverlay(ctx: CanvasRenderingContext2D, viewport: ViewportState): void;
  /** Told when a snap changes the board, for sharing and saving. */
  onEdited(handler: (edit: BoardEdit) => void): Unsubscribe;
  /** True while a shape is on its way from ink to clean form. */
  readonly morphing: boolean;
  dispose(): void;
}

/** A shape part way between the ink that was drawn and the shape it is becoming. */
interface Morph {
  /** The ink exactly as the board held it, kept so that "undo snap" is exact. */
  readonly drawn: Stroke;
  readonly object: BoardObject;
  readonly from: readonly Point[];
  readonly to: readonly Point[];
  readonly style: OutlineStyle;
  readonly startedAt: number;
}

interface State {
  readonly options: SmartPenOptions;
  readonly frames: FrameScheduler;
  readonly timer: Timer;
  readonly now: () => number;
  readonly reducedMotion: () => boolean;
  readonly handlers: Set<(edit: BoardEdit) => void>;
  readonly onSnapped: (offer: SnapOffer | null) => void;
  /** Strokes waiting out the evaluation window, by id. */
  readonly waiting: Map<string, number>;
  /** The ink still lying beside what is being judged — the company writing keeps. */
  recent: RecentInk[];
  morph: Morph | null;
  frame: number | null;
  /** The snap the teacher can still take back, and when the offer runs out. */
  offer: SnapOffer | null;
  offerTimer: number | null;
}

/** The board's own setting, when nobody has said otherwise. */
function prefersLessMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function morphDuration(state: State): number {
  return state.reducedMotion() ? 0 : SNAP_MORPH_DURATION_MS;
}

function emit(state: State, edit: BoardEdit): void {
  for (const handler of state.handlers) handler(edit);
}

/** Remembers a piece of ink as company for whatever is drawn beside it next. */
function remember(state: State, stroke: Stroke, at: number): void {
  state.recent = state.recent.filter((ink) => at - ink.at <= WRITING_MEMORY_MS);
  state.recent.push({ id: stroke.id, at, bounds: strokeBounds(stroke) });
}

/** Ink that has become a shape is no longer ink, and no longer company. */
function forget(state: State, strokeId: string): void {
  state.recent = state.recent.filter((ink) => ink.id !== strokeId);
}

/** Takes the offer of a way back off the screen, whatever ended it. */
function withdraw(state: State): void {
  if (state.offerTimer !== null) state.timer.clear(state.offerTimer);
  state.offerTimer = null;
  if (!state.offer) return;
  state.offer = null;
  state.onSnapped(null);
}

/**
 * Puts the original ink back and takes the shape away, for good (SRS C5).
 *
 * The ink returns under a new id, because the id it had went off this board when it
 * snapped and every other screen has already forgotten it. What comes back is the
 * same line, point for point and pressure for pressure — the teacher gets back
 * exactly what they drew, not a fresh guess at it.
 *
 * Permanent by construction: the restored stroke never passes back through the
 * evaluation window, so it cannot snap a second time.
 */
function undoSnap(state: State, object: BoardObject, drawn: Stroke): void {
  withdraw(state);
  // Undone by hand, or by somebody else, while the pill was still up: nothing to do.
  if (!state.options.objects.find(object.id)) return;

  const restored: Stroke = { ...drawn, id: newStrokeId(), createdAt: Date.now() };
  state.options.objects.remove(object.id);
  state.options.ink.accept(restored);
  // Ink again, so anything written beside it next is recognised as writing.
  remember(state, restored, state.now());
  emit(state, { ...NO_EDIT, removed: [object.id], added: [restored] });
  state.options.onChanged('static');
}

/** SRS C5 — for four seconds after a shape lands, there is a way back. */
function offerUndo(state: State, object: BoardObject, drawn: Stroke): void {
  withdraw(state);
  const offer: SnapOffer = {
    object,
    undo: () => {
      undoSnap(state, object, drawn);
    },
  };
  state.offer = offer;
  state.offerTimer = state.timer.set(() => {
    state.offerTimer = null;
    withdraw(state);
  }, UNDO_SNAP_PILL_DURATION_MS);
  state.onSnapped(offer);
}

/** The shape arrives on the board, and the board hears about it once. */
function land(state: State): void {
  const morph = state.morph;
  if (!morph) return;
  state.morph = null;
  state.options.objects.put(morph.object);
  emit(state, { ...NO_EDIT, removed: [morph.drawn.id], objectsAdded: [morph.object] });
  state.options.onChanged('overlay');
  offerUndo(state, morph.object, morph.drawn);
}

/** Keeps the overlay painting until the morph is home, then stops for good. */
function keepMorphing(state: State): void {
  if (state.frame !== null) return;
  state.frame = state.frames.request(() => {
    state.frame = null;
    const morph = state.morph;
    if (!morph) return;
    if (state.now() - morph.startedAt >= morphDuration(state)) {
      land(state);
      return;
    }
    state.options.onChanged('overlay');
    keepMorphing(state);
  });
}

/** How the snapped shape is drawn: exactly as the ink it replaces was drawn. */
function styleOf(stroke: Stroke): OutlineStyle {
  return { color: stroke.color, canvasWidth: stroke.canvasWidth, opacity: stroke.opacity };
}

/**
 * Judges one stroke, once its evaluation window is up.
 *
 * Everything is checked again at this moment rather than remembered from pen-up: the
 * stroke may have been rubbed out, moved or undone in the meantime, and the setting
 * may have been turned off. A stroke that is no longer on the board is simply left.
 */
function evaluate(state: State, strokeId: string): void {
  state.waiting.delete(strokeId);
  if (!state.options.enabled()) return;

  const stroke = state.options.ink.find(strokeId);
  if (!stroke) return;
  if (drawnWithHighlighter(stroke)) return;

  // SRS C5 — asked before the recogniser, because the answer to "is this a circle?"
  // is yes for a written nought, and the recogniser is right to say so.
  const bounds = strokeBounds(stroke);
  if (tooSmallToSnap(bounds)) return;
  const beside = state.recent.filter((ink) => ink.id !== strokeId);
  if (looksLikeWriting(bounds, state.now(), beside)) return;

  const drawn: Point[] = strokePoints(stroke).map((point) => ({ x: point.x, y: point.y }));
  const match = state.options.recogniser.recognise(drawn, state.options.confidence());
  if (!match) return;

  const clean = cleanShapeOf(match);
  if (!clean) return;

  const object = buildShape({
    kind: clean.kind,
    shapeType: clean.shapeType,
    points: clean.points,
    userId: stroke.userId,
    color: stroke.color,
    canvasWidth: stroke.canvasWidth,
    layer: stroke.layer,
    ...(clean.sides ? { sides: clean.sides } : {}),
  });

  // One shape morphs at a time: a second snap puts the first one down where it is.
  land(state);
  if (!state.options.ink.remove(strokeId)) return;
  forget(state, strokeId);

  state.morph = {
    drawn: stroke,
    object,
    from: drawn,
    to: clean.points,
    style: styleOf(stroke),
    startedAt: state.now(),
  };
  state.options.onChanged('static');
  state.options.onChanged('overlay');

  if (morphDuration(state) <= 0) land(state);
  else keepMorphing(state);
}

function consider(state: State, stroke: Stroke): void {
  // Remembered even when the Smart Pen is off, so that turning it on mid-sentence
  // does not snap the next word.
  remember(state, stroke, state.now());
  if (!state.options.enabled() || state.waiting.has(stroke.id)) return;
  const handle = state.timer.set(() => {
    evaluate(state, stroke.id);
  }, SNAP_EVALUATION_WINDOW_MS);
  state.waiting.set(stroke.id, handle);
}

function paintOverlay(
  state: State,
  ctx: CanvasRenderingContext2D,
  viewport: ViewportState,
): void {
  const morph = state.morph;
  if (!morph) return;
  const duration = morphDuration(state);
  const share = duration <= 0 ? 1 : (state.now() - morph.startedAt) / duration;
  const points = morphBetween(morph.from, morph.to, Math.max(0, Math.min(1, share)));
  drawOutline(ctx, points, morph.style, toViewport(viewport));
}

export function createSmartPen(options: SmartPenOptions): SmartPen {
  const state: State = {
    options,
    frames: options.frames ?? animationFrames,
    timer: options.timer ?? systemTimer,
    now: options.now ?? (() => Date.now()),
    reducedMotion: options.reducedMotion ?? prefersLessMotion,
    handlers: new Set<(edit: BoardEdit) => void>(),
    onSnapped: options.onSnapped ?? (() => undefined),
    waiting: new Map<string, number>(),
    recent: [],
    morph: null,
    frame: null,
    offer: null,
    offerTimer: null,
  };

  return {
    consider: (stroke) => {
      consider(state, stroke);
    },
    paintOverlay: (ctx, viewport) => {
      paintOverlay(state, ctx, viewport);
    },
    onEdited: (handler) => {
      state.handlers.add(handler);
      return () => {
        state.handlers.delete(handler);
      };
    },
    get morphing() {
      return state.morph !== null;
    },
    dispose() {
      withdraw(state);
      for (const handle of state.waiting.values()) state.timer.clear(handle);
      state.waiting.clear();
      state.recent = [];
      if (state.frame !== null) state.frames.cancel(state.frame);
      state.frame = null;
      state.morph = null;
    },
  };
}
